import { getAccessToken } from '@/lib/tokenStorage';

export type AuditLogItemValue = {
  id: string | null;
  value: string;
};

export type AuditLogChange = {
  id: string;
  columnName: string;
  displayName: string;
  description: string;
  dataType: string | null;
  originalValue: AuditLogItemValue | null;
  newValue: AuditLogItemValue | null;
};

export type AuditLogItem = {
  id: string;
  operationType: string;
  operationTimestamp: string;
  userEmail: string | null;
  metadata: string | null;
  changes: AuditLogChange[];
};

export type AuditLogCollection = {
  totalCount: number;
  items: AuditLogItem[];
  entityDisplayName: string | null;
  hasMore: boolean;
};

export type AuditHistoryParams = {
  objectId: string;
  skip?: number;
  take?: number;
  q?: string;
  userEmail?: string;
  operationType?: string;
  startDate?: string;
  endDate?: string;
  languageCode?: string;
};

function getApiBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getAuthHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

export async function fetchAuditHistory(
  params: AuditHistoryParams,
  signal?: AbortSignal,
): Promise<AuditLogCollection> {
  const search = new URLSearchParams();
  search.set('objectId', params.objectId);
  if (params.skip != null && params.skip > 0)
    search.set('skip', String(params.skip));
  if (params.take != null) search.set('take', String(params.take));
  if (params.q) search.set('q', params.q);
  if (params.userEmail) search.set('userEmail', params.userEmail);
  if (params.operationType) search.set('operationType', params.operationType);
  if (params.startDate) search.set('startDate', params.startDate);
  if (params.endDate) search.set('endDate', params.endDate);
  search.set('languageCode', params.languageCode ?? 'en-US');

  const url = `${getApiBaseUrl()}/api/v1/AuditLog/history?${search.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching audit history`);
  }

  return await response.json();
}

export type AuditFeedItem = {
  id: string;
  objectId: string;
  objectType: string;
  entityDisplayName: string;
  recordName: string | null;
  operationType: string;
  operationTimestamp: string;
  userEmail: string | null;
  changedFields: string[];
};

export type AuditFeedCollection = {
  items: AuditFeedItem[];
  hasMore: boolean;
};

export type AuditFeedParams = Omit<AuditHistoryParams, 'objectId'> & {
  objectType?: string;
};

export type AuditEntityType = {
  objectType: string;
  displayName: string;
};

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: getAuthHeaders(),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} fetching ${path.split('?')[0]}`);
  }

  return await response.json();
}

/** Recent audit events across records (optionally one entity type), newest first. */
export async function fetchAuditFeed(
  params: AuditFeedParams,
  signal?: AbortSignal,
): Promise<AuditFeedCollection> {
  const search = new URLSearchParams();
  if (params.objectType) search.set('objectType', params.objectType);
  if (params.skip != null && params.skip > 0)
    search.set('skip', String(params.skip));
  if (params.take != null) search.set('take', String(params.take));
  if (params.q) search.set('q', params.q);
  if (params.userEmail) search.set('userEmail', params.userEmail);
  if (params.operationType) search.set('operationType', params.operationType);
  if (params.startDate) search.set('startDate', params.startDate);
  if (params.endDate) search.set('endDate', params.endDate);
  search.set('languageCode', params.languageCode ?? 'en-US');

  return getJson(`/api/v1/audit/feed?${search.toString()}`, signal);
}

export async function fetchAuditEntityTypes(
  languageCode = 'en-US',
  signal?: AbortSignal,
): Promise<AuditEntityType[]> {
  return getJson(
    `/api/v1/audit/entity-types?languageCode=${encodeURIComponent(languageCode)}`,
    signal,
  );
}

export const auditApi = {
  fetchAuditHistory,
  fetchAuditFeed,
  fetchAuditEntityTypes,
};
